import {
  enableProdMode,
  LOCALE_ID,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
  isDevMode,
} from '@angular/core';
import { APP_CONFIG, ENV_CONFIG } from '@core/interfaces/configs';
import { APPCONFIG, ENVCONFIG, TMX } from '@core/tokens/configs';
import { LoggerService } from '@core/services/logger.service';

import { CONFIG } from './app/core/constants/config';
import { AppComponent } from './app/app.component';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { ApiModule as CoreApiModule } from '@api/api.module';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { CoreEffects } from '@store/core/core.effects';
import { EffectsModule } from '@ngrx/effects';
import { reducers } from '@store/core/core.reducer';
import { StoreModule } from '@ngrx/store';
import { AppRoutingModule } from './app/routes/app-routing.module';
import { provideAnimations } from '@angular/platform-browser/animations';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { Configuration } from '@api/configuration';
import { HeaderInterceptor } from '@core/interceptors/header.interceptor';
import { TimeoutInterceptor } from '@core/interceptors/timeout.interceptor';
import { Router } from '@angular/router';
import { ErrorInterceptor, AuthHeaderInterceptor, DpopInterceptor } from '@core/interceptors';
import { HTTP_INTERCEPTORS, withInterceptorsFromDi, provideHttpClient } from '@angular/common/http';
import { DpopService } from '@core/services/dpop.service';
import { LanguageService } from '@core/services/language.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { initializeLang, gtmConfigFactory, apiConfigFactory } from '@core/factories';
import { MatDialogModule } from '@angular/material/dialog';
import { UtilService } from '@core/services/util.service';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { TmxService } from '@core/services/tmx.service';
import { NgxMaskService, provideNgxMask } from 'ngx-mask';
import frCA from '@angular/common/locales/fr-CA';
import { registerLocaleData } from '@angular/common';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { CookieAcceptService } from '@cookie/cookie-accept.service';
import { provideLottieOptions } from 'ngx-lottie';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideServiceWorker } from '@angular/service-worker';
fetch('/env.json')
  .then((res) => res.json())
  .then((envConfig: ENV_CONFIG) => {
    fetch('/assets/app-config.json')
      .then((res) => res.json())
      .then((appConfig: APP_CONFIG) => {
        if (envConfig.production) {
          enableProdMode();
        }
        registerLocaleData(frCA);
        return bootstrapApplication(AppComponent, {
          providers: [
            provideZoneChangeDetection(),
            importProvidersFrom(
              BrowserModule,
              MatDialogModule,
              MatBottomSheetModule,

              AppRoutingModule,
              TranslateModule.forRoot({
                fallbackLang: 'en-CA',
              }),
              StoreModule.forRoot(reducers),
              EffectsModule.forRoot([CoreEffects]),
              StoreDevtoolsModule.instrument({
                maxAge: 25,
                logOnly: true,
                autoPause: true, // Pauses recording actions and state changes when the extension window is not open
              }),
              CoreApiModule,
              GoogleTagManagerModule.forRoot(),
            ),
            { provide: ENVCONFIG, useValue: envConfig },
            {
              provide: APPCONFIG,
              useValue: {
                ...CONFIG,
                ...appConfig,
              },
            },
            {
              provide: TMX,
              useValue: new TmxService(document, envConfig, appConfig, new LoggerService(envConfig)).init(),
            },
            ...provideTranslateHttpLoader({
              prefix: './assets/i18n/',
              suffix: '.json',
              useHttpBackend: true,
            }),
            provideAppInitializer(() => {
              const initializerFn = initializeLang(inject(TranslateService), inject(LanguageService));
              return initializerFn();
            }),
            provideLottieOptions({
              player: () => import('lottie-web'), // Use dynamic import for better performance
            }),
            {
              provide: HTTP_INTERCEPTORS,
              useClass: ErrorInterceptor,
              multi: true,
              deps: [APPCONFIG, Router],
            },
            {
              provide: HTTP_INTERCEPTORS,
              useClass: AuthHeaderInterceptor,
              multi: true,
              deps: [],
            },
            {
              provide: HTTP_INTERCEPTORS,
              useClass: DpopInterceptor,
              multi: true,
              deps: [DpopService],
            },
            {
              provide: HTTP_INTERCEPTORS,
              useClass: TimeoutInterceptor,
              multi: true,
              deps: [APPCONFIG],
            },
            {
              provide: HTTP_INTERCEPTORS,
              useClass: HeaderInterceptor,
              multi: true,
              deps: [TranslateService],
            },
            {
              provide: Window,
              useValue: window,
            },
            {
              provide: Document,
              useValue: document,
            },
            {
              provide: LOCALE_ID,
              deps: [TranslateService],
              useFactory: (translateService: TranslateService) => translateService.getCurrentLang(),
            },
            {
              provide: 'googleTagManagerId',
              useFactory: gtmConfigFactory,
              deps: [ENVCONFIG],
            },
            { provide: Configuration, useFactory: apiConfigFactory, deps: [ENVCONFIG] },
            UtilService,
            TranslateService,
            NgxMaskService,
            provideNgxMask(),
            provideAnimations(),
            provideHttpClient(withInterceptorsFromDi()),
            CookieAcceptService,
            { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
            { provide: 'LOCATION', useValue: window.location },
            provideServiceWorker('ngsw-worker.js', {
              enabled: !isDevMode(),
              registrationStrategy: 'registerWhenStable:30000',
            }),
          ],
        }).catch((err) => console.error(err));
      });
  });
